import jsonServerProvider from "ra-data-json-server";
import { fetchUtils } from "react-admin";
import { ATS_API_URL as API_URL, MAIN_APP_URL } from "../config";


let isRefreshing = false;
let refreshPromise: Promise<void> | null = null;
let queuedRequests: Array<() => void> = [];

function getTokenExpiry(token: string | null | undefined): number | null {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

async function ensureTokenValid(): Promise<void> {
  const authRaw = localStorage.getItem('auth');
  const { data }: AuthLocalStorage = authRaw ? JSON.parse(authRaw) : {};
  const token = data?.tokendata;
  const expiry = getTokenExpiry(token);
  if (expiry && Date.now() > expiry - 5000) { // 5s leeway
    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = refreshToken();
    }
    await refreshPromise;
  }
}

async function refreshToken(): Promise<void> {
  try {
    const localStorageData: AuthLocalStorage = JSON.parse(<any>localStorage.getItem("auth")) || {};
    const requestOptions: RequestInit = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshtoken: localStorageData?.data?.refreshtoken }),
    };
    const response: Response = await fetch(`${API_URL}/auth/refreshtoken`, requestOptions);
    if (!response.ok) {
      localStorage.removeItem("auth");
      window.location.replace(MAIN_APP_URL);
      throw new Error('Refresh failed');
    } else {
      const res = await response.json();
      localStorage.removeItem("auth");
      localStorage.setItem(
        "auth",
        JSON.stringify({
          ...localStorageData,
          data: {
            ...localStorageData?.data,
            tokendata: res?.data?.tokendata,
            refreshtoken: res?.data?.refreshtoken,
          },
        })
      );
    }
  } finally {
    isRefreshing = false;
    if (refreshPromise) {
      queuedRequests.forEach(cb => cb());
      queuedRequests = [];
      refreshPromise = null;
    }
  }
}

interface AuthData {
  tokendata?: string | null;
  refreshtoken?: string | null;
  trn_id?: string | null;
}

interface AuthLocalStorage {
  data?: AuthData;
  [key: string]: any;
}

type FetchJsonResponse = {
  status: number;
  headers: Headers;
  body: string;
  json: any;
};


const httpClient = async (
  url: string,
  options: fetchUtils.Options = {}
): Promise<FetchJsonResponse> => {
  if (!options.headers) {
    options.headers = new Headers({ Accept: "application/json" });
  }
  const method = String(options.method || "GET").toUpperCase();
  if (method === "GET" && API_URL && url.startsWith(API_URL)) {
    options.cache = "no-store";
    (options.headers as Headers).set("Cache-Control", "no-cache, no-store, must-revalidate");
    (options.headers as Headers).set("Pragma", "no-cache");
    (options.headers as Headers).set("Expires", "0");
  }
  // Proactively check token expiry and refresh if needed
  await ensureTokenValid();
  const { data }: AuthLocalStorage = JSON.parse(<any>localStorage.getItem("auth")) || {};
  (options.headers as Headers).set("authorization", data?.tokendata || "");
  (options.headers as Headers).set("trn_id", data?.trn_id || "");
  try {
    return await fetchUtils.fetchJson(url, options);
  } catch (error: any) {
    // If 401, try refresh (queue if already refreshing)
    if (error.status === 401) {
      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = refreshToken();
      }
      // Queue this request until refresh completes
      return new Promise((resolve, reject) => {
        queuedRequests.push(async () => {
          try {
            await refreshPromise;
            const { data }: AuthLocalStorage = JSON.parse(<any>localStorage.getItem("auth")) || {};
            (options.headers as Headers).set("authorization", data?.tokendata || "");
            resolve(await fetchUtils.fetchJson(url, options));
          } catch (e) {
            reject(e);
          }
        });
      });
    }
    const message =
      error?.body?.message ??
      error?.body?.error ??
      error?.message ??
      "Request failed";
    throw { ...error, message };
  }
};

const publicHttpClient = async (
  url: string,
  options: fetchUtils.Options = {}
): Promise<FetchJsonResponse> => {
  if (!options.headers) {
    options.headers = new Headers({ Accept: "application/json" });
  }

  return fetchUtils.fetchJson(url, options);
};

const appendAuthHeaders = (headers: Headers) => {
  const authRaw = localStorage.getItem("auth");
  const { data }: AuthLocalStorage = authRaw ? JSON.parse(authRaw) : {};
  headers.set("authorization", data?.tokendata || "");
  headers.set("trn_id", data?.trn_id || "");
};

export const fetchWithAuth = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  const fetchOnce = async () => {
    await ensureTokenValid();
    const headers = new Headers(options.headers || {});
    appendAuthHeaders(headers);
    return fetch(url, { ...options, headers });
  };

  let response = await fetchOnce();
  if (response.status === 401) {
    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = refreshToken();
    }
    await refreshPromise;
    response = await fetchOnce();
  }
  return response;
};

const baseDataProvider = jsonServerProvider(API_URL, httpClient);

export default baseDataProvider;
export { httpClient, publicHttpClient };
